import { CommonPageWrapper } from 'admin/components/wrappers';
import CustomBreadCrumb from 'app/components/CustomBreadCrumb';
import styled from 'styled-components';
import { PageMainLabel } from 'app/components/SHLabel';
import { breadCrumpitems } from './staticDatas';
import { AdminSearch } from 'app/components/AdminSearchField';
import { useEffect, useState } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import SponsersTable from './SponsersTable';
import useSponserApi from 'admin/hooks/useSponserApi';
import { usePermission } from 'hooks/usePermission';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { useNavigateToRoute } from 'utils/common';
import { CommonLoader } from 'app/components/CommonLoader';
import { SortDescriptor } from '@progress/kendo-data-query';
import AdminDropDown from 'app/components/AdminDropDown';
import { GeneralLookup } from 'services/openapi';

type Props = {};

const SponsersPage = (props: Props) => {
  const redirect = useNavigateToRoute();
  const {
    fetchSponsersList,
    sponsersDataList,
    deleteSponsor,
    loading,
    downloadSponsersList,
  } = useSponserApi();
  const { hasViewPermission } = usePermission();
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debounceRiderSearch = useDebounce(searchTerm);
  const [pageSkip, setPageSkip] = useState<number | undefined>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [pageSizeSelected, setPageSizeSelected] = useState<GeneralLookup>({
    id: '50',
    name: '50',
  });
  const pageSizeOptions: GeneralLookup[] = [
    { id: '25', name: '25' },
    { id: '50', name: '50' },
    { id: '100', name: '100' },
    { id: '200', name: '200' },
  ];
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const initialSort: Array<SortDescriptor> = [{ field: 'name', dir: 'asc' }];
  const [sort, setSort] = useState<Array<SortDescriptor>>(initialSort);
  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };
  const handlePageSizeChange = (data: GeneralLookup) => {
    const newPageSize = parseInt(data.id, 10);
    setPageSize(newPageSize);
    setPageSizeSelected(data);
    setPageSkip(0);
    fetchSponsersList(
      debounceRiderSearch,
      sortField,
      sortOrder,
      0,
      newPageSize,
    );
  };

  const handleDownloadSponsor = async () => {
    setDownloadLoading(true);
    try {
      const url = await downloadSponsersList(
        debounceRiderSearch,
        sortField,
        sortOrder,
      );
      // Extract filename from URL
      const filename = url.split('/').pop();

      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename!; // Use the extracted filename

          // Append link to body (required for Firefox)
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(objectUrl);
        })
        .catch(error => {
          console.error('Download failed:', error);
          setDownloadLoading(false);
        });

      return url;
    } catch (error) {
      throw error;
    } finally {
      setDownloadLoading(false);
    }
  };
  useEffect(() => {
    fetchSponsersList(
      debounceRiderSearch,
      sortField,
      sortOrder,
      pageSkip,
      pageSize,
    );
  }, [sortOrder]);

  useEffect(() => {
    if (debounceRiderSearch.length >= 0) {
      fetchSponsersList(debounceRiderSearch, sortField, sortOrder, 0, pageSize);
    } else {
      fetchSponsersList(
        debounceRiderSearch,
        sortField,
        sortOrder,
        pageSkip,
        pageSize,
      );
    }
    if (pageSkip !== 0) {
      if (debounceRiderSearch?.length >= 0) {
        setPageSkip(0);
      }
    }
  }, [debounceRiderSearch]);
  return (
    <CommonPageWrapper>
      {downloadLoading && <CommonLoader loaderType="main" />}
      <HeadingWrapper>
        <PageMainLabel>Sponsors</PageMainLabel>
        <CustomBreadCrumb preData={breadCrumpitems} />
      </HeadingWrapper>
      <HeadingWrapper>
        <ButtonWrapper>
          <AdminCustomButton
            buttonType="primary"
            isActive={false}
            className="fill-button"
            onClick={() => {
              redirect(`/admin/sponsors/create`);
            }}
          >
            Add New Sponsor
          </AdminCustomButton>
          <SearchWrapper>
            <AdminCustomButton
              buttonType="primary"
              isActive={false}
              className="fill-button"
              onClick={handleDownloadSponsor}
              disabled={
                typeof sponsersDataList !== 'object' ||
                sponsersDataList === null ||
                sponsersDataList === undefined
              }
            >
              Download
            </AdminCustomButton>

            <div style={{ width: '283px', paddingTop: '10px' }}>
              <AdminSearch
                placeholder="Search by sponsors"
                onSearchTextEntering={handleSearch}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
          </SearchWrapper>
        </ButtonWrapper>
      </HeadingWrapper>
      {(sponsersDataList?.pageDetails?.count ?? 0) > 10 && (
        <EntriesPerPageDropDownWrapper>
          <AdminDropDown
            label="No: of entries per page"
            onDataChange={(data: GeneralLookup) => {
              handlePageSizeChange(data);
            }}
            defaultValue={pageSizeSelected}
            data={pageSizeOptions}
          />
        </EntriesPerPageDropDownWrapper>
      )}
      {/* Sponsers Table */}
      {hasViewPermission('0800101001') && (
        <>
          {loading ? (
            <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
          ) : (
            <SponsersTable
              setPageSkip={setPageSkip}
              pageSkip={pageSkip}
              pageSize={pageSize}
              sponsersDataList={sponsersDataList}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              fetchSponsersList={fetchSponsersList}
              sortField={sortField}
              sortOrder={sortOrder}
              debounceRiderSearch={debounceRiderSearch}
              deleteSponsor={deleteSponsor}
              sort={sort}
              setSort={setSort}
            />
          )}
        </>
      )}
    </CommonPageWrapper>
  );
};

export default SponsersPage;
const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  width: 100%;
  .field-style {
    display: flex;
  }
`;
const SearchWrapper = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  align-items: baseline;
  padding: 15px 0 0 0;

  .btn-action {
    display: flex;
    align-items: center;
    gap: 15px;
  }
`;

const EntriesPerPageDropDownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  .SHdropdown {
    width: 160px;

    .label {
      margin-bottom: 8px;
      font-size: 13px;
      color: #475467;
      font-weight: 500;
    }

    .k-dropdownlist {
      max-height: 38px;
      border-radius: 8px !important;
    }

    .k-input-inner {
      padding: 0 8px !important;
      font-size: 13px;
    }

    .k-input-value-text {
      margin: 4px 0;
      font-size: 13px !important;
      color: #1e1e1e;
    }
  }
`;
